import { useState, useEffect, useRef } from "react";
import { DataTable} from "primereact/datatable";
import { Column } from "primereact/column";
import { Chart } from "primereact/chart";
import { Tag } from "primereact/tag";
import { colorPairs, formatAmount, formatDate } from "../../utils/functions";
import "primereact/resources/themes/saga-blue/theme.css";
import "primereact/resources/primereact.min.css";
import "primeicons/primeicons.css";
import KpiCard from "../../components/features/user/KpiCard";
import { Toast } from "primereact/toast";
import { Button } from "primereact/button";
import { TransactionFiltersDTO, TransactionsDto, TransactionStatusEnum } from "../../models/Transaction";
import { transactionsService } from "../../services/transactionsService";
import { customerService } from "../../services/customerService";
import Loader from "../../components/common/Loader";
import { Dialog } from "primereact/dialog";
import { Skeleton } from "primereact/skeleton";
import { useSelector } from "react-redux";
import { RootState } from "../../redux/store";


const AdminDashboard = () => {
  const [allTransactions, setAllTransactions] = useState<any[]>([]);
  const [topCustomer, setTopCustomer] = useState<any[]>([]);

 
  const toastRef = useRef<Toast>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState<any | null>(
    null
  );
  const [transactionModalVisible, setTransactionModalVisible] = useState(false);
  const { currentUser } = useSelector((state: RootState) => state.auth);
   
    const showToast = (
      severity: "success" | "error",
      summary: string,
      detail: string
    ) => {
      toastRef.current?.show({
        severity,
        summary,
        detail,
        life: 3000,
      });
    };
    
   const [data, setData] = useState({
      numTransaction: null,
      successRate: null,
      totalCollected: { totalAmount: "", currency: "" },
      topServices: [{ serviceId: "", serviceName: "", transactionCount: 0 }],
      totalCustomer:null,
      allTransactions:null,
      chartData: {},
      chartOptions: {},
    });

  const userFilters: TransactionFiltersDTO = {
    page: 1,
    limit: 99999,
  };

  const fetchAllTransactions = async () => {
    setIsLoading(true);
    try {
      const response =
        await transactionsService.getAllTransactions(userFilters);
      setAllTransactions(response.data.data);
    } catch (err) {
      toastRef.current?.show({
        severity: "warn",
        summary: "Error while fetching all transactions",
        detail: "Error while fetching all transactions",
        life: 3000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchTopCustomer = async () => {
    setIsLoading(true);
    try {
      const response = await transactionsService.getTopCustomers();
      setTopCustomer(response.data);
    } catch (err) {
      toastRef.current?.show({
        severity: "warn",
        summary: "Error while fetching top customers",
        detail: "Error while fetching top customers",
        life: 3000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchTransactionDetails = async (transactionId: string) => {
    try {
      const transaction =
        await transactionsService.transactionDetails(transactionId);
      setSelectedTransaction(transaction.data);
      setTransactionModalVisible(true);
    } catch (err) {
      toastRef.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to fetch transaction details",
        life: 3000,
      });
    }
  };

     const fetchData = async () => {
       try {
         setIsLoading(true);
   
         const [
           totalTransactionsRes,
           successRateRes,
           totalCollectedRes,
           totalCustomerRes,
           topServicesRes,
         ] = await Promise.all([
           transactionsService.getTotalTransactions(),
           transactionsService.adminGetSuccessRate(),
           transactionsService.getTotalAmount(),
           customerService.getTotalCustomer(),
           transactionsService.adminTopCustomers(),
         ]);
   
         if (
           totalTransactionsRes.status === -1 ||
           successRateRes.status === -1 ||
           totalCollectedRes.status === -1 ||
           totalCustomerRes.status === -1  ||
           topServicesRes.status === -1
         ) {
           throw new Error("One or more API calls failed");
         }
   
         setData((prev) => ({
           ...prev,
           numTransaction: totalTransactionsRes.data.totalTransactions,
           successRate: successRateRes.data.successRate,
           totalCollected: totalCollectedRes.data,
           totalCustomer: totalCustomerRes.data,
           topServices: topServicesRes.data,
         }));
   
       } catch (error: any) {
         showToast("error", "Error", error.message || "An error occurred");
       } finally {
         setIsLoading(false);
       }
     };

  const handleViewTransaction = (transactionId: string) => {
    fetchTransactionDetails(transactionId);
  };

  useEffect(() => {
    if (currentUser) {
      fetchData();
      fetchAllTransactions();
      fetchTopCustomer();
    }
  }, [currentUser]);


  const statusBodyTemplate = (status: TransactionStatusEnum

  ):"success"| "info" | "warning"| "danger" => {
    switch (status) {
       case TransactionStatusEnum.SUCCESS:
            return "success";
       case TransactionStatusEnum.INITIATED:
            return "info";
       case TransactionStatusEnum.PENDING:
            return "warning";
       case TransactionStatusEnum.FAILED:
            return "danger";
       default:
            return "info";
    }

  };

  const chartData = {
    labels: topCustomer.map((user) => user.customerName),
    datasets: [
      {
        label: "Amount",
        backgroundColor: topCustomer.map((user, index) => {
          const colors = ["rgba(54, 162, 235, 0.2)"];
          return colors[index % colors.length];
        }),
        data: topCustomer.map((user) => user.totalAmount),
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "top",
      },
    },
  };

  const kpiData = [
    {
      title: "Total Customers",
      amount: data.totalCustomer,
      icon: "pi pi-users",
    },
    {
      title: "Transactions success Rate",
      amount: `${data.successRate}%`,
      icon: "pi pi-check-circle",
    },
    {
      title: "total Amount",
      amount: `${formatAmount(data.totalCollected?.totalAmount)} ${data.totalCollected?.currency}`,
      icon: "pi pi-wallet",
    },
    {
      title: "Total Transactions",
      amount: data.numTransaction,
      icon: "pi pi-history",
    },
  ];

  return (
    <div className="admin-dashboard">
      {isLoading ? (
        <div className="load">
          <Loader />
        </div>
      ) : (
        <>
          <div className="bg-white rounded-md p-4 mb-5">
            <div className="flex items-center gap-2 mb-4 text-sm text-secondary-500">
              <i className="pi pi-database"></i>
              <p className="m-0">Key Performance Indicators</p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-4 gap-4 w-50">
              {kpiData.map((item, index) => {
                const colors = colorPairs[index % colorPairs.length];
                return item.amount !== null ? (
                  <KpiCard key={index} colors={colors} item={item} />
                ) : (
                  <div key={index} className="text-center text-red-500">
                    NA
                  </div>
                );
              })}
           </div>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="bg-white p-4 rounded-md">
              <p className="m-0">Users with most transactions rate</p>
              {isLoading ? (
                <Skeleton style={{ width: "100%", height: "16erm" }} />
              ) : topCustomer.length > 0 ? (
                <Chart
                  type="bar"
                  data={chartData}
                  options={chartOptions}
                  style={{ height: "400px" }}
                />
              ) : (
                <div className="text-center text-red-500">NA</div>
              )}
            </div>
            <div className="col-span-1 lg:col-span-1">
            <div className="bg-white rounded-md p-4">
              <div className="flex items-center gap-2 mb-4 text-sm text-secondary-500">
                <i className="pi pi-objects-column"></i>
                <p className="m-0">Top Services</p>
              </div>
              {
                data.topServices.map((service, index) => {
                  const colors = [...colorPairs].reverse()[index % colorPairs.length];
                  return (
                    <div
                      key={service.serviceId ?? index}
                      className="flex items-center gap-2 bg-white rounded-md p-3 border mb-3"
                    >
                      <div className={`h-2 w-2 rounded-full ${colors.bg}`}></div>
                      <div className={`border-l-2 ${colors.bd} rounded-s-md w-full`}>
                        <div className="pl-4 flex flex-col">
                          <div className="flex items-center justify-between">
                            <span className="font-semibold text-xs text-black">
                             {service.serviceName ?? "---"}
                            </span>
                          </div>
                          <span className="text-gray-600 text-xs">
                            Successful transactions: {service.transactionCount ?? "0"}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })
              }
            </div>
            </div>
          </div>
          <div className="bg-white p-4 mt-4 shadow-md rounded-md">
            <p className="text-black mb-4">Last transactions</p>
            <DataTable
              value={allTransactions}
              paginator
              rows={3}
              tableClassName="text-sm"
              rowsPerPageOptions={[5, 10, 25, 50]}
              paginatorTemplate="
                FirstPageLink
                PrevPageLink
                PageLinks NextPageLink
                LastPageLink
                CurrentPageReport
                RowsPerPageDropdown"
              currentPageReportTemplate="Showing {first} to {last} of {totalRecords} record"
              filterDisplay="menu"
              globalFilterFields={[
                "trans_code",
                "customer_name",
                "customer_email",
                "amount",
                "status",
                "commission",
              ]}
            >
              <Column field="trans_code" header="Code" sortable />
              <Column field="customer_name" header="Customer" sortable />
              <Column
                field="amount"
                header="Amount"
                body={(rowData) => (
                  <div>
                    {rowData.amount} {rowData.currency}
                  </div>
                )}
                sortable
              />
              <Column
                field="commission"
                header="Commission"
                body={(rowData) => (
                  <div>
                    {rowData.commission} {rowData.currency}
                  </div>
                )}
                sortable
              />
              <Column
                field="status"
                header="Status"
                 body={(rowData: TransactionsDto) => (
                <Tag
                  value={rowData.status}
                  severity={statusBodyTemplate(rowData.status as TransactionStatusEnum)}
                />
                  )}
                sortable
              />
              <Column
                field="createdAt"
                header="Date"
                body={(rowData) => formatDate(rowData.createdAt)}
                sortable
              />
              <Column
                body={(rowData) => (
                  <div className="flex flex-wrap gap-2">
                    <Button
                      label="Details"
                      className="bg-blue-500 text-white px-2 py-1 rounded hover:bg-blue-600"
                      onClick={() => handleViewTransaction(rowData.id)}
                    />
                  </div>
                )}
                header="Option"
              />
            </DataTable>
            <Dialog
              header="Transaction Details"
              visible={transactionModalVisible}
              closeIcon="pi pi-times"
              className="p-fluid bg-white rounded-lg shadow-lg max-w-xl w-full"
              onHide={() => setTransactionModalVisible(false)}
            >
              {selectedTransaction ? (
                <div className="p-6 space-y-4">
                  <div className="grid grid-cols-2 gap-4 border-b pb-2">
                    <div className="text-left text-gray-900 font-medium">
                      Transaction Number:
                    </div>
                    <div className="text-right text-gray-700">
                      {selectedTransaction.trans_code}
                    </div>
                    <div className="text-left text-gray-900 font-medium">
                      User:
                    </div>
                    <div className="text-right text-gray-700">
                      {selectedTransaction.customer_name}
                    </div>

                    <div className="text-left text-gray-900 font-medium">
                      Phone:
                    </div>
                    <div className="text-right text-gray-700">
                      {selectedTransaction.customer_msisdn}
                    </div>

                    <div className="text-left text-gray-900 font-medium">
                      Country Code:
                    </div>
                    <div className="text-right text-gray-700">
                      {selectedTransaction.countryCode.toUpperCase()}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4 border-b pb-2">
                    <div className="text-left text-gray-900 font-medium">
                      Service:
                    </div>
                    <div className="text-right text-gray-700">
                      {
                        selectedTransaction.subscription.partnerService.service
                          .name
                      }
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4 border-b pb-2">
                    <div className="text-left text-gray-900 font-medium">
                      Amount:
                    </div>
                    <div className="text-right text-gray-700">
                      {selectedTransaction.amount}{" "}
                      {selectedTransaction.currency}
                    </div>

                    <div className="text-left text-gray-900 font-medium">
                      Date:
                    </div>
                    <div className="text-right text-gray-700">
                      {formatDate(selectedTransaction.createdAt)}
                    </div>

                    <div className="text-left text-gray-900 font-medium">
                      Status:
                    </div>
                    <div
                      className={`text-right font-bold ${
                        selectedTransaction.status === "SUCCESS"
                          ? "text-green-600"
                          : selectedTransaction.status === "Pending"
                            ? "text-blue-600"
                            : "text-secondary-600"
                      }`}
                    >
                      {selectedTransaction.status}
                    </div>
                  </div>
                </div>
              ) : (
                <p className="text-center text-gray-500">
                  <Loader />
                </p>
              )}
            </Dialog>
          </div>
        </>
      )}
    </div>
  );
};

export default AdminDashboard;


